import { useMemo, useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { TableTitleBox, PaginationTable } from '../../common/components';
import { LoadingSkeleton } from '../../common/components';
import DebtDetailFilters from './components/DebtDetailFilters';
import DebtDetailSummary from './components/DebtDetailSummary';
import DebtDetailActionsBar from './components/DebtDetailActionsBar';
import DebtDetailDataTable from './components/DebtDetailDataTable';
import SendNotificationsDialog from './components/SendNotificationsDialog';
import DebtEmailResultsDialog from '../common/components/DebtEmailResultsDialog';
import { useDebtDetailTable } from './hooks/useDebtDetailTable';
import { exportDebtDetailToExcel, exportDebtDetailToPDF } from './utils/debtDetailUtils';
import { normalizeDebtEmailResults } from '../common/utils/emailResultUtils';

const DebtDetailTable = () => {
  const {
    plans,
    groups,
    plansLoading,
    groupsLoading,
    filter,
    page,
    totalPages,
    totalCount,
    data,
    summary,
    isLoading,
    isFetching,
    exportFilters,
    possessorOptions,
    selectedMap,
    selectedSummary,
    notificationResults,
    resultsDialogOpen,
    openResultsDialog,
    closeResultsDialog,
    isSelectingAll,
    selectAllContracts,
    handleFilterChange,
    handlePossessorChange,
    handleResetFilters,
    handlePageChange,
    rowsPerPage,
    handleRowsPerPageChange,
    toggleCouponSelection,
    toggleRowSelection,
    isCouponSelected,
    isRowFullySelected,
    clearSelection,
    buildSendPayload,
    sendDebtMailsMutation,
  } = useDebtDetailTable();

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportInProgress, setExportInProgress] = useState(null);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const selectedEntries = useMemo(
    () =>
      Object.values(selectedMap).map((entry) => ({
        header: entry.header,
        coupons: Object.values(entry.coupons || {}),
      })),
    [selectedMap]
  );

  const isPreparingExport = Boolean(exportInProgress);
  const disableExportButtons = data.length === 0 || isPreparingExport;
  const excelLoading = exportInProgress === 'excel';
  const pdfLoading = exportInProgress === 'pdf';
  const totalContracts = summary?.contracts_total ?? summary?.contracts_on_page ?? data.length;
  const allContractsSelected =
    totalContracts > 0 && selectedEntries.length >= totalContracts;

  const handleExportExcel = async () => {
    if (exportInProgress) {
      return;
    }

    setExportInProgress('excel');
    try {
      await exportDebtDetailToExcel({
        filters: exportFilters,
      });
      setSnackbar({
        open: true,
        message: 'Reporte en Excel generado correctamente.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error al exportar el detalle de mora a Excel:', error);
      setSnackbar({
        open: true,
        message: 'No se pudo generar el archivo Excel.',
        severity: 'error',
      });
    } finally {
      setExportInProgress(null);
    }
  };

  const handleExportPdf = async () => {
    if (exportInProgress) {
      return;
    }

    setExportInProgress('pdf');
    try {
      await exportDebtDetailToPDF({
        filters: exportFilters,
      });
      setSnackbar({
        open: true,
        message: 'Reporte PDF generado correctamente.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error al exportar el detalle de mora a PDF:', error);
      setSnackbar({
        open: true,
        message: 'No se pudo generar el archivo PDF.',
        severity: 'error',
      });
    } finally {
      setExportInProgress(null);
    }
  };

  const handleConfirmSendNotifications = async ({ message, includeAmounts = false } = {}) => {
    const payload = buildSendPayload({ message, includeAmounts });
    try {
      const result = await sendDebtMailsMutation.mutateAsync(payload);

      const normalizedResults = normalizeDebtEmailResults(result, { payload });
      openResultsDialog(normalizedResults);

      const hasFailures = (normalizedResults.summary?.failed_count ?? 0) > 0;
      setSnackbar({
        open: true,
        message: normalizedResults.message,
        severity: hasFailures ? 'warning' : 'success',
      });

      clearSelection();
      setSendDialogOpen(false);
    } catch (error) {
      console.error(error);
      const apiResult = error?.response?.data;
      const errorMessage =
        apiResult?.message ||
        apiResult?.error ||
        error?.message ||
        'Ocurrió un error al enviar las notificaciones.';

      const fallbackResult = {
        message: errorMessage,
        failed_details: (payload.emails_deudas || []).map((email) => ({
          suscriptor: email.suscriptor,
          contrato_id: email.contrato_id,
          email: email.email,
          error: errorMessage,
        })),
      };

      const normalizedResults = normalizeDebtEmailResults(apiResult || fallbackResult, { payload });

      openResultsDialog(normalizedResults);

      const hasSuccesses = (normalizedResults.summary?.successful_count ?? 0) > 0;
      const hasFailures = (normalizedResults.summary?.failed_count ?? 0) > 0;
      const severity = hasFailures ? (hasSuccesses ? 'warning' : 'error') : 'success';
      setSnackbar({
        open: true,
        message: normalizedResults.message,
        severity,
      });

      clearSelection();
      setSendDialogOpen(false);
    }
  };

  return (
    <Box>
      <TableTitleBox title="Habitat 99 | Mora | Detalle de Mora" />

      <DebtDetailFilters
        filter={filter}
        plans={plans}
        groups={groups}
        possessorOptions={possessorOptions}
        onFilterChange={handleFilterChange}
        onPossessorChange={handlePossessorChange}
        onResetFilters={handleResetFilters}
        loadingPlans={plansLoading}
        loadingGroups={groupsLoading}
      />

      {isLoading && !data.length ? (
        <LoadingSkeleton rows={8} />
      ) : (
        <>
          <DebtDetailSummary
            summary={summary}
            selectedSummary={selectedSummary}
            isLoading={isFetching}
          />

          <DebtDetailActionsBar
            onExportExcel={handleExportExcel}
            onExportPdf={handleExportPdf}
            onSelectAllContracts={selectAllContracts}
            onSendNotifications={() => setSendDialogOpen(true)}
            onClearSelection={clearSelection}
            disableExports={disableExportButtons}
            isExporting={isPreparingExport}
            excelLoading={excelLoading}
            pdfLoading={pdfLoading}
            disableSelectAll={
              totalContracts === 0 || allContractsSelected || isSelectingAll
            }
            isSelectAllLoading={isSelectingAll}
            disableSend={selectedSummary.selectedCoupons === 0}
            disableClear={selectedSummary.selectedCoupons === 0}
          />

          <DebtDetailDataTable
            data={data}
            isLoading={isLoading || isFetching}
            onToggleCoupon={toggleCouponSelection}
            onToggleRow={toggleRowSelection}
            isCouponSelected={isCouponSelected}
            isRowFullySelected={isRowFullySelected}
          />

          {totalCount > rowsPerPage && (
            <PaginationTable
              totalPages={totalPages}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </>
      )}

      <SendNotificationsDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        onConfirm={handleConfirmSendNotifications}
        selectedEntries={selectedEntries}
        isSubmitting={sendDebtMailsMutation.isPending}
      />

      <DebtEmailResultsDialog
        open={resultsDialogOpen}
        onClose={closeResultsDialog}
        results={notificationResults}
        title="Resultado del envío de recordatorios de mora"
        successSectionLabel="Correos enviados"
        errorSectionLabel="Correos con error"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DebtDetailTable;
